
const INVALID_XML_CHARS = new RegExp('[\u0000-\u0008\u000B\u000C\u000E-\u001F\uFFFE\uFFFF]', 'g');
import { DEMAND_TYPE, FILE_EXTENSION, POSSESSIVE_PRONOUN, SUBJECT_PRONOUN, OBJECT_PRONOUN } from '@demandproai/shared';

export function stripInvalidXmlCharacters(value) {
  if (typeof value === 'string') return value.replace(INVALID_XML_CHARS, '');
  if (Array.isArray(value)) return value.map(stripInvalidXmlCharacters);
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, stripInvalidXmlCharacters(v)]));
  }
  return value;
}

export function getValidNumber(value) {
  if (typeof value === "number") return isNaN(value) ? 0 : value;
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  const sanitized = typeof value === "string"
    ? value.replace(/[$,\s]/g, "")
    : value;
  const num = Number(sanitized);

  return isNaN(num) ? 0 : num;
}

export function formatCurrency(value) {
  const validValue = getValidNumber(value);
  return validValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

export function isValidObjectId(value) {
  return /^[0-9a-fA-F]{24}$/.test(String(value || ""));
}

async function retryLoop(fn, shouldRetry, maxRetries, initialDelay) {
  let retries = 0;

  while (retries < maxRetries) {
    try {
      return await fn();
    } catch (error) {
      if (!shouldRetry(error)) throw error;

      retries++;
      if (retries >= maxRetries) {
        console.error(`Failed after ${maxRetries} retries:`, error);
        throw error;
      }

      // Exponential backoff
      const delay = initialDelay * Math.pow(2, retries);
      console.log(`Retry ${retries}/${maxRetries} after ${delay}ms...`);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  throw new Error('Unexpected error in retryLoop');
}

export async function withRetry(fn, maxRetries = 3, initialDelay = 100) {
  return retryLoop(fn, () => true, maxRetries, initialDelay);
}

export async function withRetryOnError(fn, retryableErrors, maxRetries = 2, initialDelay = 100) {
  const errorTypes = Array.isArray(retryableErrors) ? retryableErrors : [retryableErrors];
  return retryLoop(fn, (error) => errorTypes.some((ErrorType) => error instanceof ErrorType), maxRetries, initialDelay);
}

export const getDBName = (domainName) => {
  if (!domainName) throw new Error('domainName is required');
  const DBName = domainName.split('.')[0]?.trim();
  if (!DBName) throw new Error(`database not found: ${domainName}`);
  return DBName;
};

export const getDomainNameFromDbName = (dbName) => `${dbName}.demandpro.law`;

export const getDemandText = ({ state, caseType, value, demandNameMap }) => {
  if (!value) return '';
  if (state && caseType) {
    const key = `${state}__${caseType}__${value}`;
    if (demandNameMap?.[key]) {
      return demandNameMap[key];
    }
  }
  return DEMAND_TYPE[value]?.text || value;
};

export const replaceExtensionWithPDF = (path) => {
  return path.replace(/\.docx?$/, `.${FILE_EXTENSION.PDF}`);
};

export const toPdfPath = (value) => (value ? replaceExtensionWithPDF(value) : value);

export const getHisHer = (isPlural, isMale) => {
  if (isPlural) return POSSESSIVE_PRONOUN.NEUTRAL;
  return isMale ? POSSESSIVE_PRONOUN.MALE : POSSESSIVE_PRONOUN.FEMALE;
};

export const getHeShe = (isPlural, isMale, isCaps = false) => {
  const pronoun = isPlural ? SUBJECT_PRONOUN.NEUTRAL : (isMale ? SUBJECT_PRONOUN.MALE : SUBJECT_PRONOUN.FEMALE);
  return isCaps ? pronoun.charAt(0).toUpperCase() + pronoun.slice(1) : pronoun;
};

export const getHimHer = (isPlural, isMale) => {
  if (isPlural) return OBJECT_PRONOUN.NEUTRAL;
  return isMale ? OBJECT_PRONOUN.MALE : OBJECT_PRONOUN.FEMALE;
};

// generates a numeric string id used for client, defendant and attorney records
export const generateRandomIds = () => {
  const randomDigits = Math.floor(10000 + Math.random() * 9000000000);
  return String(randomDigits);
};

// flattens a yup ValidationError into a readable "path: message" list
export const formatYupValidationError = (error) => {
  const inner = Array.isArray(error?.inner) ? error.inner : [];
  if (!inner.length) return error?.message || 'validation failed';
  return inner
    .map((innerError) => `${innerError?.path || '(root)'}: ${innerError?.message || 'invalid'}`)
    .join('; ');
};

export const tryOrLogDecorator = (fn, message, logger = console.warn, defaultReturn = null) => {
  return async (...args) => {
    try {
      return await fn(...args);
    }
    catch (error) {
      logger(message, error);
      return defaultReturn;
    }
  }
}

export const occationalProcessLogDecorator = (asyncFn, message, frequency)=>{
  return (...args) =>{
    const timerId = setInterval(()=>{
      console.log(message)
    }, frequency);
    const result = asyncFn(...args);
    clearInterval(timerId);
    return result;
  }
}